/** @format */

import React, { useEffect } from 'react';
import { Highlight } from 'react-instantsearch';
import { sanitizeJson } from '@services/string-transformation-services';
import Translate from '@components/translation/translate-text-wrapper';
import { isPastDate } from '@services/date-services';
import { startLazyLoad } from '@services/lazy-load';

/**
 * Search Result.
 */
const SearchResult = ({ hit }): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { url, pageType, pageData } = hit;
  const safePageData = pageData ? sanitizeJson(pageData) : null;

  /**
   * Lazy load images.
   */
  useEffect(() => {
    if (pageType === 'blog' || pageType === 'product') {
      setTimeout(() => startLazyLoad(), 50);
    }
  });

  /**
   * Blog
   */
  return (
    <a
      href={
        pageType === 'event' &&
        safePageData?.eventLinkUrl !== '' &&
        safePageData?.eventLinkUrl.indexOf('internal') == -1
          ? safePageData?.eventLinkUrl
          : url
      }>
      <article
        className={`search-result ${pageType} ${
          !safePageData?.imageId ? 'no-image' : ''
        }`}>
        {(pageType === 'blog' || pageType === 'product') &&
          safePageData?.imageId && (
            <div className="img-container">
              <img
                data-src={`${safePageData?.imageId}?w=400&h=400&crop=true`}
                alt={safePageData?.title}
                className="lazy img-responsive"
              />
            </div>
          )}
        <div className="text-container center-vertically">
          {pageType !== 'event' && (
            <h3>
              <Highlight attribute="title" hit={hit} />
            </h3>
          )}

          {/* Product */}
          {pageType === 'product' &&
            url?.toLowerCase().indexOf('veterinary') > -1 && (
              <h4 className="font-size-base-sm">
                <span className="category veterinary-oncology">
                  <Translate>Veterinary Oncology</Translate>
                </span>
              </h4>
            )}

          {/* Blog */}
          {pageType === 'blog' && (
            <h4 className="font-size-base-sm">
              <span className="category blog">
                {safePageData?.category === 'China' ||
                safePageData?.category === 'Finland'
                  ? `${safePageData?.category} Blog`
                  : safePageData?.category}
              </span>{' '}
              <time className="date">{safePageData?.articleDate}</time>
            </h4>
          )}

          {/* News */}
          {pageType === 'news' && (
            <h4 className="font-size-base-sm">
              <span className="category press-release">
                <Translate>Press Release</Translate>
              </span>{' '}
              <time className="date">{safePageData?.articleDate}</time>
            </h4>
          )}

          {/* Course */}
          {pageType === 'course' && (
            <h4 className="font-size-base-sm">
              <span className="region">{safePageData?.courseRegion}</span>
              {safePageData?.courseTimeZone && (
                <>
                  <span className="dot-separator">&#x2022;</span>
                  <span className="timezone">
                    {safePageData?.courseTimeZone}
                  </span>
                </>
              )}
            </h4>
          )}

          {/* Event */}
          {pageType === 'event' && !isPastDate(safePageData?.eventEndDate) && (
            <>
              <h3>
                <Highlight attribute="title" hit={hit} />
              </h3>
              <h4 className="font-size-base-sm">
                {isPastDate(safePageData?.eventEndDate) && (
                  <>
                    <span className="course-unavailable background-accent-yellow">
                      <Translate>Event passed</Translate>
                    </span>
                  </>
                )}

                <time className="date">
                  {safePageData?.eventStartDates !== '' &&
                    `${safePageData.eventStartDates} - `}{' '}
                  {safePageData?.eventEndDate}
                </time>
              </h4>
              <h5 className="font-size-base-sm">{safePageData?.eventCity}</h5>
            </>
          )}

          <p>
            <Highlight attribute="description" hit={hit} />
          </p>
        </div>
      </article>
    </a>
  );
};
export default SearchResult;
